import {
  createBankAccount,
  deleteBankAccount,
  startReconciliation,
  useListBankAccounts,
  useListReconciliations,
  type BankAccountIn,
  type BankAccountOut,
  type ReconciliationSummaryOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatMoney, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';

const BOOK_ACCOUNT: Record<string, string> = {
  '1000': 'Operating Cash',
  '1010': 'Trust Cash',
};

const ACCOUNT_FIELDS: Field[] = [
  { name: 'name', label: 'Name', required: true, helperText: 'Like Operating Checking' },
  { name: 'institution', label: 'Bank' },
  { name: 'last_four', label: 'Last Four Digits', helperText: 'Never the full account number' },
  {
    name: 'account_code',
    label: 'Account on the Books',
    type: 'select',
    required: true,
    options: Object.entries(BOOK_ACCOUNT).map(([value, label]) => ({ value, label })),
  },
];

const START_FIELDS: Field[] = [
  { name: 'statement_date', label: 'Statement End Date', type: 'date', required: true },
  { name: 'statement_balance', label: 'Ending Balance', type: 'number', required: true },
];

const HISTORY_COLUMNS: DataColumn<ReconciliationSummaryOut>[] = [
  { label: 'Account', render: (r) => r.bank_account_name },
  { label: 'Statement Date', render: (r) => formatDate(r.statement_date) },
  { label: 'Ending Balance', align: 'right', render: (r) => formatMoney(r.statement_balance) },
  {
    label: 'Status',
    render: (r) =>
      r.status === 'finished' ? (
        <Chip size="small" color="success" label="Finished" />
      ) : (
        <Chip size="small" color="warning" label="In Progress" />
      ),
  },
  {
    label: 'Open',
    align: 'right',
    render: (r) => (
      <Button size="small" component={Link} to={`/staff/accounting/banking/${r.id}`}>
        Open
      </Button>
    ),
  },
];

export function BankReconciliationPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = useCan('accounting.manage');
  const accounts = okItems(useListBankAccounts().data);
  const history = okItems(useListReconciliations().data);
  const [adding, setAdding] = useState(false);
  const [starting, setStarting] = useState<BankAccountOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  const columns: DataColumn<BankAccountOut>[] = [
    { label: 'Name', render: (a) => a.name },
    { label: 'Bank', render: (a) => a.institution ?? '–' },
    { label: 'Last Four', render: (a) => a.last_four ?? '–' },
    { label: 'On the Books As', render: (a) => BOOK_ACCOUNT[a.account_code] },
    { label: 'Book Balance', align: 'right', render: (a) => formatMoney(a.book_balance) },
    {
      label: 'Last Reconciled',
      render: (a) => (a.last_reconciled_on ? formatDate(a.last_reconciled_on) : 'Never'),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (a) =>
        canManage && (
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button size="small" variant="outlined" onClick={() => setStarting(a)}>
              Reconcile
            </Button>
            <Button
              size="small"
              color="error"
              onClick={() => {
                if (!window.confirm(`Remove ${a.name}?`)) return;
                setError(null);
                deleteBankAccount(a.id)
                  .then(() => queryClient.invalidateQueries())
                  .catch((e: unknown) => setError(apiErrorMessage(e)));
              }}
            >
              Remove
            </Button>
          </Stack>
        ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">Bank Reconciliation</Typography>
          <Typography color="text.secondary">
            Match your books to each bank statement, by ticking what appears on it. Nothing here
            connects to your bank.
          </Typography>
        </div>
        {canManage && (
          <Button variant="contained" onClick={() => setAdding(true)}>
            Add bank account
          </Button>
        )}
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      <DataTable columns={columns} rows={accounts} rowKey={(a) => a.id} />
      {accounts.length === 0 && (
        <Alert severity="info">Add a bank account to reconcile your first statement.</Alert>
      )}
      <Typography variant="h6">Statements</Typography>
      <DataTable columns={HISTORY_COLUMNS} rows={history} rowKey={(r) => r.id} />
      {adding && (
        <FormDialog
          title="Add Bank Account"
          fields={ACCOUNT_FIELDS}
          initial={{ name: '', institution: '', last_four: '', account_code: '1000' }}
          onSubmit={(values) =>
            createBankAccount({
              name: required(values, 'name'),
              institution: optional(values, 'institution'),
              last_four: optional(values, 'last_four'),
              account_code: required(values, 'account_code') as BankAccountIn['account_code'],
            })
          }
          onClose={() => setAdding(false)}
        />
      )}
      {starting && (
        <FormDialog
          title={`Reconcile ${starting.name}`}
          intro="Enter the end date and ending balance from the bank statement."
          fields={START_FIELDS}
          initial={{ statement_date: todayIso(), statement_balance: '' }}
          submitLabel="Start"
          onSubmit={async (values) => {
            const made = okData(
              await startReconciliation({
                bank_account_id: starting.id,
                statement_date: required(values, 'statement_date'),
                statement_balance: required(values, 'statement_balance'),
              }),
            );
            if (made) await navigate(`/staff/accounting/banking/${made.id}`);
          }}
          onClose={() => setStarting(null)}
        />
      )}
    </Stack>
  );
}
